import { TripPayload } from 'store/trips/trips.types'
import { Order } from 'store/orders/orders.types'
import { LoadConfirmationUserDetails } from 'store/load-confirmation/load-confirmation.api'
import {
  defaultLoadConfirmationFormValues,
  LoadConfirmationFormValues,
} from '../schema'
import { CarrierContactDetails } from './carrier-contact'

export function resolveSourceOrderId(
  tripTasks: TripPayload[],
  orders: Order[]
): number | null {
  const transportTask = tripTasks.find(
    (task) => task.type === 'PICKUP' || task.type === 'DELIVERY'
  )
  if (!transportTask) return null

  if (transportTask.sourceOrderPkId) {
    return transportTask.sourceOrderPkId
  }

  if (!transportTask.orderId) return null

  const order = orders.find((o) => o.userOrderId === transportTask.orderId)
  return order?.orderId ?? null
}

function tripTaskToStop(task: TripPayload) {
  return {
    startTime: task.startTime ?? '',
    endTime: task.estimatedTime ?? task.startTime ?? '',
    name: task.name ?? '',
    address: task.address ?? '',
    loadType: 'FTL',
    loadCount: '',
    temperatureValue: '',
    hazmat: '',
    pickupNumber: '',
    weightValue: '',
    notes: '',
    contactName: '',
    contactPhone: '',
  }
}

function orderEventToStop(event: Order['orderEvents'][number]) {
  return {
    startTime: event?.startTime ?? '',
    endTime: event?.endTime ?? event?.startTime ?? '',
    name: event?.name ?? '',
    address: event?.address ?? '',
    loadType: event?.loadType ?? 'FTL',
    loadCount: event?.loadCount ? String(event.loadCount) : '',
    temperatureValue:
      event?.temperatureValue || event?.temperatureValue === 0
        ? String(event.temperatureValue)
        : '',
    hazmat: event?.hazmat ?? '',
    pickupNumber: event?.pickupNumber ?? '',
    weightValue:
      event?.weightValue || event?.weightValue === 0 ? String(event.weightValue) : '',
    notes: event?.notes ?? '',
    contactName: '',
    contactPhone: '',
  }
}

function mergeTripTasksWithExistingStops(
  tripTasks: TripPayload[],
  existingOrder: Order | null | undefined,
  eventType: 'PICKUP' | 'DELIVERY'
) {
  const tasks = tripTasks.filter((task) => task.type === eventType)
  const existingStops = (existingOrder?.orderEvents ?? [])
    .filter((event) => event.eventType === eventType)
    .map(orderEventToStop)

  if (tasks.length === 0 && existingStops.length > 0) {
    return existingStops
  }

  return tasks.map((task, index) => {
    const tripStop = tripTaskToStop(task)
    const existingStop = existingStops[index]
    if (!existingStop) return tripStop
    return {
      ...existingStop,
      // Keep event metadata from current trip so newly added events always show up.
      startTime: task.startTime ?? '',
      endTime: task.estimatedTime ?? task.startTime ?? '',
      name: task.name ?? '',
      address: task.address ?? '',
    }
  })
}

export function buildFormFromTripTasks(
  tripTasks: TripPayload[],
  userDetails?: LoadConfirmationUserDetails | null,
  existingOrder?: Order | null,
  tripCarrier?: {
    carrierName?: string
    carrierCompanyId?: string
  } | null,
  carrierContact?: CarrierContactDetails | null
): LoadConfirmationFormValues {
  const pickups = mergeTripTasksWithExistingStops(tripTasks, existingOrder, 'PICKUP')
  const deliveries = mergeTripTasksWithExistingStops(
    tripTasks,
    existingOrder,
    'DELIVERY'
  )

  return {
    ...defaultLoadConfirmationFormValues,
    myCompanyName: userDetails?.name ?? '',
    myCompanyAddress: userDetails?.address ?? '',
    issuedBy: userDetails?.issuedBy ?? '',
    myCompanyEmail: userDetails?.dispatchEmail ?? '',
    myCompanyPhone: userDetails?.phone ?? '',
    myCompanyCell: userDetails?.cell ?? '',
    myCompanyAfterHoursPhone: userDetails?.afterHoursPhone ?? '',
    invoicePodContactName: userDetails?.invoicePodContactName ?? '',
    invoicePodEmail: userDetails?.invoicePodEmail ?? '',
    invoicePodPhone: userDetails?.invoicePodPhone ?? '',
    carrierName: tripCarrier?.carrierName ?? existingOrder?.customerName ?? '',
    carrierAddress: carrierContact?.carrierAddress ?? '',
    dispatchName: carrierContact?.dispatchName ?? '',
    dispatchEmail:
      existingOrder?.notificationEmail ?? carrierContact?.dispatchEmail ?? '',
    dispatchPhone: carrierContact?.dispatchPhone ?? '',
    dispatchCell: carrierContact?.dispatchCell ?? '',
    dispatchAfterHours: carrierContact?.dispatchAfterHours ?? '',
    selectedCarrierCompanyId:
      tripCarrier?.carrierCompanyId ?? existingOrder?.companyId ?? '',
    currency: existingOrder?.currency ?? '',
    baseRate:
      existingOrder?.baseRate || existingOrder?.baseRate === 0
        ? String(existingOrder.baseRate)
        : '',
    detentionCharges:
      existingOrder?.detentionCharges || existingOrder?.detentionCharges === 0
        ? String(existingOrder.detentionCharges)
        : '',
    layoverCharges:
      existingOrder?.layoverCharges || existingOrder?.layoverCharges === 0
        ? String(existingOrder.layoverCharges)
        : '',
    fuelSurcharge:
      existingOrder?.fuelSurcharge || existingOrder?.fuelSurcharge === 0
        ? String(existingOrder.fuelSurcharge)
        : '',
    otherCharges:
      existingOrder?.otherCharges || existingOrder?.otherCharges === 0
        ? String(existingOrder.otherCharges)
        : '',
    notes: existingOrder?.notes ?? userDetails?.note ?? '',
    pickups: pickups.length > 0 ? pickups : [],
    deliveries: deliveries.length > 0 ? deliveries : [],
  }
}

export function mapStopsToOrderEvents(form: LoadConfirmationFormValues) {
  const pickupEvents = (form.pickups ?? []).map((stop) => ({
    name: stop?.name ?? '',
    address: stop?.address ?? '',
    startTime: stop?.startTime || null,
    endTime: stop?.endTime || null,
    eventType: 'PICKUP' as const,
    loadType: (stop?.loadType === 'RTL' ? 'RTL' : 'FTL') as 'FTL' | 'RTL',
    loadCount: parseInt(String(stop?.loadCount || '0'), 10) || 0,
    temperatureValue: parseFloat(String(stop?.temperatureValue || '0')) || 0,
    temperatureUnit: 'C',
    hazmat: stop?.hazmat ?? '',
    weightValue: parseFloat(String(stop?.weightValue || '0')) || 0,
    weightUnit: 'lb',
    pickupNumber: stop?.pickupNumber ?? '',
    notes: stop?.notes ?? '',
  }))

  const deliveryEvents = (form.deliveries ?? []).map((stop) => ({
    name: stop?.name ?? '',
    address: stop?.address ?? '',
    startTime: stop?.startTime || null,
    endTime: stop?.endTime || null,
    eventType: 'DELIVERY' as const,
    loadType: (stop?.loadType === 'RTL' ? 'RTL' : 'FTL') as 'FTL' | 'RTL',
    loadCount: parseInt(String(stop?.loadCount || '0'), 10) || 0,
    temperatureValue: parseFloat(String(stop?.temperatureValue || '0')) || 0,
    temperatureUnit: 'C',
    hazmat: stop?.hazmat ?? '',
    weightValue: parseFloat(String(stop?.weightValue || '0')) || 0,
    weightUnit: 'lb',
    pickupNumber: stop?.pickupNumber ?? '',
    notes: stop?.notes ?? '',
  }))

  return [...pickupEvents, ...deliveryEvents]
}
